import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const SHOWN_KEY = "ps_popup_notifs_shown";
const POLL_MS = 45000;

function loadShown() {
  try { return new Set(JSON.parse(localStorage.getItem(SHOWN_KEY) || "[]")); }
  catch { return new Set(); }
}
function saveShown(set) {
  try { localStorage.setItem(SHOWN_KEY, JSON.stringify([...set].slice(-300))); } catch { /* */ }
}

/**
 * ITER208: notificaciones emergentes globales.
 * Consulta las notificaciones no leídas y muestra un toast (y una notificación
 * nativa del navegador si el usuario dio permiso) por cada alerta nueva de
 * tareas o eventos programados.
 */
export default function NotificationPopups() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const shown = useRef(loadShown());

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/tareas/notificaciones?solo_no_leidas=true");
      const limite = Date.now() - 30 * 60 * 1000;
      const items = data?.items || [];
      // Solo emergen alertas RECIENTES (últimos 30 min). Las anteriores se
      // marcan como vistas para no saturar la pantalla al abrir la app.
      const nuevas = items.filter(n => {
        if (shown.current.has(n.id)) return false;
        const t = Date.parse(n.created_at || "");
        if (!Number.isFinite(t) || t < limite) { shown.current.add(n.id); return false; }
        return true;
      });
      // Mostrar máximo 4 popups por ciclo para no saturar la pantalla
      nuevas.slice(0, 4).forEach(n => {
        shown.current.add(n.id);
        const titulo = n.tarea_titulo || n.titulo || "Notificación";
        const cuerpo = n.texto_preview || n.mensaje || "";
        toast(titulo, {
          description: cuerpo,
          duration: 15000,
          action: {
            label: "Ver",
            onClick: () => navigate(n.url || "/tareas"),
          },
        });
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          try {
            const nat = new Notification(titulo, { body: cuerpo, tag: n.id });
            nat.onclick = () => { window.focus(); navigate(n.url || "/tareas"); };
          } catch { /* */ }
        }
      });
      nuevas.slice(4).forEach(n => shown.current.add(n.id));
      saveShown(shown.current);
    } catch { /* silencioso: no interrumpir la app */ }
  }, [navigate]);

  useEffect(() => {
    if (!user || user === false) return;
    poll();
    const id = setInterval(poll, POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, [user, poll]);

  return null;
}
